import * as React from 'react';
import { tabbable } from 'tabbable';
import { findBlurRelatedTarget } from './DomUtil';
import { DivAttrs } from './HtmlAttrs';

function handleFocus(e: React.FocusEvent<HTMLElement>): void {
  e.stopPropagation(); // just for TFS 739813 and 740525, c.f. PortalEventTrap
}

function handleBlur(e: React.FocusEvent<HTMLElement>, onTrap?: React.FocusEventHandler): void {
  const relatedTarget = findBlurRelatedTarget(e);
  if (relatedTarget && !e.currentTarget.contains(relatedTarget)) {
    (e.target as HTMLElement).focus();
    e.stopPropagation();

    if (onTrap) {
      onTrap(e);
    }
  }
}

export type FocusTrapProps = { onTrap?: React.FocusEventHandler<HTMLElement> } & Omit<DivAttrs, 'onBlur' | 'onFocus'>;

/**
 * Prevents focus from leaving a container by re-focusing the previously focused element if it is determined that focus would leave the container.
 * Note: this does not work in firefox, which would require a settimeout() before re-focusing.
 * If this ever gets re-enabled for firefox, make sure to re-enable the unit tests.
 */
export const FocusTrap: React.FunctionComponent<FocusTrapProps> = (props) => {
  const { onTrap, ...divProps } = props;
  const handleBlurCallback = React.useCallback(
    (e: React.FocusEvent<HTMLElement>) => {
      handleBlur(e, onTrap);
    },
    [onTrap],
  );

  return <div {...divProps} onBlur={handleBlurCallback} onFocus={handleFocus} data-focus-trap='' />;
};

/** Keeps keyboard focus within a container by wrapping focus when Tabbing or shift+Tabbing beyond the last or first tabbable element */
export const wrapFocusOnTabKeyDown: React.KeyboardEventHandler<HTMLElement> = e => {
  if (e.key === 'Tab') {
    // trap focus in container
    const tabbables = tabbable(e.currentTarget);
    const first = tabbables[0];
    const last = tabbables[tabbables.length - 1];

    if (e.target === last && !e.shiftKey) {
      e.preventDefault();
      first?.focus();
    } else if (e.target === first && e.shiftKey) {
      e.preventDefault();
      last?.focus();
    }
  }
};
